import { useTranslation } from "react-i18next";
import { FilePdf, Receipt } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import { formatLocalYmd } from "@/lib/company-expiry";
import { COMPANY_INVOICE_STATUS_CONFIG } from "@/lib/status-configs";
import type { CompanyInvoiceSummary } from "@/types/company-billing";
import { formatInvoicePeriod } from "./use-supervisor-billing";

const TH = "px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground";

function displayStatus(inv: CompanyInvoiceSummary, today: string): keyof typeof COMPANY_INVOICE_STATUS_CONFIG {
  if (inv.status === "issued" && inv.dueAt && inv.dueAt < today) return "overdue";
  return inv.status;
}

function StatusPill({ inv, today }: { inv: CompanyInvoiceSummary; today: string }) {
  const cfg = COMPANY_INVOICE_STATUS_CONFIG[displayStatus(inv, today)];
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold", cfg.bg, cfg.color)}>
      <span className={cn("size-1.5 rounded-full", cfg.dot)} />
      {cfg.label}
    </span>
  );
}

/** Period, ili "Dopuna kredita · datum" za fakturu za dopunu. */
function PeriodLabel({ inv }: { inv: CompanyInvoiceSummary }) {
  const { t } = useTranslation();
  const period = formatInvoicePeriod(inv);
  return <>{inv.kind === "credit_topup" ? `${t("supervisor.billing.invoices.creditTopup")} · ${period}` : period}</>;
}

/** Stornirana faktura ostaje u listi (arhiva), ali iznos nije dug: precrtan. */
function Amount({ inv }: { inv: CompanyInvoiceSummary }) {
  const { t } = useTranslation();
  const isVoid = inv.status === "void";
  return (
    <span
      className={cn("tabular-nums", isVoid && "text-muted-foreground line-through")}
      title={isVoid && inv.voidReason ? `${t("supervisor.billing.invoices.voidReason")}: ${inv.voidReason}` : undefined}
    >
      {formatCurrency(inv.total)}
    </span>
  );
}

function PdfLink({ id, label }: { id: string; label: string }) {
  return (
    <a
      href={`/api/company-invoices/${id}/pdf`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <FilePdf className="size-3.5" /> {label}
    </a>
  );
}

export function InvoicesTable({ invoices, today }: { invoices: CompanyInvoiceSummary[]; today: string }) {
  const { t } = useTranslation();

  return (
    <section className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm">
      <div className="border-b border-border/40 px-5 py-3.5">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          {t("supervisor.billing.invoices.heading")}
        </h2>
      </div>

      {invoices.length === 0 ? (
        <div className="flex flex-col items-center px-5 py-10 text-center">
          <Receipt className="size-6 text-muted-foreground/60" />
          <p className="mt-2 text-sm text-muted-foreground">{t("supervisor.billing.invoices.empty")}</p>
        </div>
      ) : (
        <>
          <table className="hidden w-full md:table">
            <thead>
              <tr className="bg-muted/30">
                <th className={TH}>{t("supervisor.billing.invoices.number")}</th>
                <th className={TH}>{t("supervisor.billing.invoices.period")}</th>
                <th className={cn(TH, "text-right")}>{t("supervisor.billing.invoices.amount")}</th>
                <th className={TH}>{t("supervisor.billing.invoices.dueAt")}</th>
                <th className={TH}>{t("supervisor.billing.invoices.status")}</th>
                <th className={TH}><span className="sr-only">PDF</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {invoices.map((inv) => (
                <tr key={inv.id} className={cn("transition-colors hover:bg-muted/20", inv.status === "void" && "opacity-70")}>
                  <td className="whitespace-nowrap px-5 py-3.5 text-sm font-medium tabular-nums text-foreground">
                    {inv.invoiceNumber ?? "–"}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-sm text-muted-foreground">
                    <PeriodLabel inv={inv} />
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-semibold text-foreground">
                    <Amount inv={inv} />
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-sm text-muted-foreground">
                    {inv.dueAt && inv.status === "issued" ? formatLocalYmd(inv.dueAt) : "–"}
                  </td>
                  <td className="px-5 py-3.5"><StatusPill inv={inv} today={today} /></td>
                  <td className="px-5 py-3.5 text-right"><PdfLink id={inv.id} label="PDF" /></td>
                </tr>
              ))}
            </tbody>
          </table>

          <ul className="divide-y divide-border/30 md:hidden">
            {invoices.map((inv) => (
              <li key={inv.id} className={cn("space-y-2 px-4 py-3.5", inv.status === "void" && "opacity-70")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold tabular-nums text-foreground">{inv.invoiceNumber ?? "–"}</p>
                    <p className="text-xs text-muted-foreground"><PeriodLabel inv={inv} /></p>
                  </div>
                  <StatusPill inv={inv} today={today} />
                </div>
                <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span>
                    <span className="text-sm font-semibold text-foreground"><Amount inv={inv} /></span>
                    {inv.dueAt && inv.status === "issued" && ` · ${t("supervisor.billing.invoices.dueAt")}: ${formatLocalYmd(inv.dueAt)}`}
                  </span>
                  <PdfLink id={inv.id} label="PDF" />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
